/**
 * M1 Catalog — frontend mirror of the closed-set enums shipped in
 * `backend/src/modules/catalog/enums.ts` (issue #80).
 *
 * Why this file exists (per #83):
 *   - The backend tightened `unit`, `category` (ingredient + dish),
 *     and `slot` (combo) to closed enums at the service zod boundary.
 *   - The frontend catalog schemas + slot editor previously typed
 *     these as free-form text — operators could type a value the
 *     backend would reject with a 400, with no UI hint that the
 *     value was off-list.
 *   - This module exposes a typed, ordered `as const` array per
 *     enum so the schema files + slot editor can drive `<Select>`
 *     options off a single source of truth.
 *
 * Cross-repo sync is enforced by
 * `frontend/src/__tests__/architecture/catalog-enums-sync.test.ts`
 * which reads the backend enums file at test time and asserts each
 * tuple matches set-equal (order-insensitive). A backend addition
 * or rename without a matching frontend bump fails CI immediately.
 *
 * Long-term plan (per #83 method A): surface the catalog enum
 * definitions on the generated OpenAPI contract (`npm run spec:gen` ->
 * backend/openapi.json -> frontend `npm run spec:types` ->
 * src/types/api.generated.ts) so the frontend types derive from the
 * spec automatically. Until that lands, this file + the sync test is
 * the bridge.
 *
 * **Promotion trigger** — when any of the following is true, this
 * bridge must be retired in favour of method A (generated types):
 *   - the catalog gains a 5th enum (4 → 5: the bridge maintenance
 *     cost passes the threshold where method A pays for itself);
 *   - any enum here is shared with a non-catalog module (e.g. an
 *     M5 / M6 caller imports `CanonicalUnit` directly);
 *   - the `catalog-enums-sync.test.ts` fails twice within six
 *     months due to backend-side reformatting alone (false-
 *     positive friction has materialised).
 * Search anchor for the future M5+ PR author: `#83 method A trigger`.
 *
 * Adding a new enum member:
 *   1. Append to `backend/src/modules/catalog/enums.ts`.
 *   2. Append to the matching `as const` array here.
 *   3. Add the i18n keys (`<entity>.enum.<field>.<value>`) to
 *      `frontend/src/i18n/locales/en.json` + `zh.json`.
 *   4. Run the suite — `catalog-enums-sync.test.ts` confirms parity.
 */

/** Canonical 6-value set for `Ingredient.unit` and recipe-line
 *  unit. Mirrors `backend/src/modules/catalog/enums.ts`
 *  `CANONICAL_UNITS`. */
export const CANONICAL_UNITS = ['g', 'kg', 'ml', 'l', 'piece', 'bunch'] as const;
export type CanonicalUnit = (typeof CANONICAL_UNITS)[number];

/** Closed set for `Ingredient.category`. Mirrors backend
 *  `INGREDIENT_CATEGORIES`. */
export const INGREDIENT_CATEGORIES = [
  'protein',
  'vegetable',
  'seasoning',
  'sauce',
  'staple',
  'other',
] as const;
export type IngredientCategory = (typeof INGREDIENT_CATEGORIES)[number];

/** Closed set for `Dish.category`. Mirrors backend `DISH_CATEGORIES`. */
export const DISH_CATEGORIES = ['stir_fry', 'soup', 'cold', 'staple', 'other'] as const;
export type DishCategory = (typeof DISH_CATEGORIES)[number];

/** Closed set for `ComboDish.slot`. Mirrors backend `COMBO_SLOTS`. */
export const COMBO_SLOTS = ['main', 'main2', 'main3', 'side', 'soup', 'staple'] as const;
export type ComboSlot = (typeof COMBO_SLOTS)[number];
